import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useTheme,
  lighten,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useIntl } from "react-intl";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import TitledCard from "../TitledCard";
import { TemplateListItemProps } from "../types";
import { MeasRuleValue, Rule, RuleBase, RuleType } from "./types";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import TimeTextField from "../TimeTextField";
import { useRuleTypes } from "./ThermowatchTemplate";
import ZoneEvent from "../zoneevent/ZoneEvent";
import { valueEdited } from "../utils";
import { onNumberInputClick } from "../../../../utils/utils";

interface AlarmItemProps extends TemplateListItemProps<Rule<MeasRuleValue[]>> {}
interface AlarmItemRowProps extends Omit<TemplateListItemProps<MeasRuleValue>, "onRestore"> {
  ruleKey: keyof RuleBase<MeasRuleValue[]>;
}

const AlarmItem = ({ index, value, saved, onChange, onDelete, onRestore, disabled }: AlarmItemProps) => {
  const [name, setName] = useState(value.name || "");
  const [rules, setRules] = useState<RuleBase<MeasRuleValue[]> | null>(null);

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const theme = useTheme();
  const ruleTypes = useRuleTypes();

  const addRule = useCallback(
    (type: keyof RuleBase<MeasRuleValue[]>) => {
      const newRule = {} as MeasRuleValue;
      setRules((old) => {
        if (!old) {
          old = {};
        }
        if (!old[type]) {
          old[type] = [];
        }
        old[type] = [...(old[type] as MeasRuleValue[]), newRule];
        value[type] = [...(old[type] as MeasRuleValue[])];

        return old;
      });
      onChange(index, value);
    },
    [value, index, onChange]
  );

  const removeRule = useCallback(
    (type: keyof RuleBase<MeasRuleValue[]>) => {
      if (!rules || rules[type] === undefined) {
        return;
      }
      delete value[type];
      setRules((old) => {
        if (!old) {
          return old;
        }
        delete old[type];
        return { ...old };
      });
      onChange(index, value);
    },
    [rules, value, index, onChange]
  );

  const onRuleChange = useCallback(
    (key: keyof RuleBase<MeasRuleValue[]>, newValue: MeasRuleValue[]) => {
      setRules((old) => {
        if (!old) {
          return old;
        }
        old[key] = newValue;
        return { ...old };
      });
      value[key] = newValue;
      onChange(index, value);
    },
    [value, index, onChange]
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { uuid, name, deleted, ...other } = value;
    setName(name || "");
    setRules(other);
  }, [value]);

  const addRules = useMemo(
    () =>
      Object.entries(ruleTypes).map(([key, value]: [string, RuleType]) => (
        <Grid item key={`rule-${key}`}>
          <Button variant="outlined" startIcon={value.icon} color="secondary" onClick={() => addRule(key as keyof RuleBase<MeasRuleValue[]>)}>
            {value.label}
          </Button>
        </Grid>
      )),
    [ruleTypes, addRule]
  );

  const ruleItems = useMemo(
    () =>
      Object.entries(ruleTypes)
        .filter(([key]) => rules?.[key as keyof RuleBase<MeasRuleValue[]>] !== undefined)
        .map(([key, v]: [string, RuleType]) => (
          <RuleItem
            key={`rule-${key}`}
            ruleKey={key as keyof RuleBase<MeasRuleValue[]>}
            type={v}
            value={rules?.[key as keyof RuleBase<MeasRuleValue[]>] as MeasRuleValue[]}
            saved={saved ? (saved[key as keyof RuleBase<MeasRuleValue[]>] as MeasRuleValue[]) : null}
            onChange={onRuleChange}
            onDelete={removeRule}
            disabled={disabled || value.deleted}
          />
        )),
    [rules, ruleTypes, disabled, value, saved, onRuleChange, removeRule]
  );

  return (
    <TitledCard
      title={
        (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") +
        formatMessage({ defaultMessage: "Alarm{name, select, null { #{index}} other {: {name}}}" }, { name: name || null, index: name ? null : index + 1 })
      }
      color={value.deleted ? editedBackground : disabled ? lighten(theme.palette.primary.main, 0.5) : theme.palette.primary.main}
      contrastColor={value.deleted ? editedText : theme.palette.secondary.contrastText}
    >
      <Grid container spacing={1} padding={1}>
        <Grid item xs={12} container flexDirection="row-reverse" alignContent="flex-start" justifyContent="space-between">
          <Grid item>
            {!value.deleted && !disabled && (
              <Button startIcon={<DeleteForeverOutlinedIcon />} color="error" onClick={() => onDelete(index)}>
                {formatMessage({ defaultMessage: "Remove" })}
              </Button>
            )}
            {value.deleted && !disabled && (
              <Button color="inherit" onClick={() => onRestore(index)}>
                {formatMessage({ defaultMessage: "Restore" })}
              </Button>
            )}
          </Grid>
          <Grid item alignSelf="flex-start" flex={1}>
            <TextField
              label={formatMessage({ defaultMessage: "Name" })}
              variant="filled"
              value={name}
              className={saved?.name !== name ? editedStyle.input : ""}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                value.name = name;
                onChange(index, value);
              }}
              size="small"
              sx={{ minWidth: 220, width: 220, margin: 0 }}
              disabled={disabled || value.deleted}
            />
          </Grid>
        </Grid>
        {ruleItems}
        {addRules.length > 0 && !disabled && !value.deleted && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Add rule:" })}</Typography>
            </Grid>
            <Grid item xs={12} container spacing={1} flexDirection="row">
              {addRules}
            </Grid>
          </>
        )}
      </Grid>
    </TitledCard>
  );
};

interface RuleItemProps {
  ruleKey: keyof RuleBase<MeasRuleValue[]>;
  type: RuleType;
  value: MeasRuleValue[];
  saved: MeasRuleValue[] | null;
  disabled?: boolean;
  onChange: (key: keyof RuleBase<MeasRuleValue[]>, value: MeasRuleValue[]) => void;
  onDelete: (key: keyof RuleBase<MeasRuleValue[]>) => void;
}

const RuleItem = ({ ruleKey, type, value, saved, disabled, onChange, onDelete }: RuleItemProps) => {
  const [indexes, setIndexes] = useState<number[]>([]);

  const theme = useTheme();

  const onItemChange = useCallback(
    (index: number, newValue: MeasRuleValue) => {
      value[index] = newValue;
      onChange(ruleKey, value);
    },
    [ruleKey, value, onChange]
  );

  const removeItem = useCallback(
    (index: number) => {
      value.splice(index, 1);
      if (value.length === 0) {
        setIndexes([]);
        onDelete(ruleKey);
      } else {
        setIndexes(value.map((_, i) => i));
        onChange(ruleKey, [...value]);
      }
    },
    [value, ruleKey, onChange, onDelete]
  );

  useEffect(() => {
    setIndexes(value.map((_, i) => i));
  }, [value]);

  return (
    <>
      {indexes.map((i) => (
        <Grid key={`rule-${ruleKey}-${i}`} item xs={12}>
          <TitledCard
            title={type.label}
            headerIcon={type.icon}
            outlined
            color={disabled ? lighten(theme.palette.secondary.main, 0.5) : theme.palette.secondary.main}
            postfix={
              !disabled && (
                <IconButton onClick={() => removeItem(i)} sx={{ mx: 1, p: 0 }}>
                  <DeleteForeverOutlinedIcon />
                </IconButton>
              )
            }
          >
            <AlarmItemRow
              ruleKey={ruleKey}
              value={value[i]}
              saved={saved?.[i] || null}
              index={i}
              disabled={disabled}
              onChange={onItemChange}
              onDelete={removeItem}
            />
          </TitledCard>
        </Grid>
      ))}
    </>
  );
};

const AlarmItemRow = ({ value, index, saved, onChange, disabled, ruleKey }: AlarmItemRowProps) => {
  const [ruleType, setRuleType] = useState("");
  const [v, setV] = useState(`${ruleType === "min" ? value.min : value.max}`);
  const [time, setTime] = useState(`${value.t}`);
  const [events, setEvents] = useState({ alarm: value.e || "", recovery: value.r || "" });

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const ruleTypes = useRuleTypes();

  const ruleTypeObject = useMemo(() => ruleTypes[ruleKey], [ruleKey, ruleTypes]);

  const handleRuleTypeChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      setRuleType(e.target.value);
      if (e.target.value === "min") {
        value.min = parseFloat(v);
        delete value.max;
      } else {
        value.max = parseFloat(v);
        delete value.min;
      }
      onChange(index, value);
    },
    [value, index, v, onChange]
  );

  useEffect(() => {
    setRuleType(() => {
      const type = Object.keys(value).includes("max") ? "max" : "min";
      if (type === "min" && value.min) {
        setV(!isNaN(value.min) ? `${value.min}` : "");
      } else if (type === "max" && value.max) {
        setV(!isNaN(value.max) ? `${value.max}` : "");
      } else {
        setV("");
      }
      return type;
    });
    setTime(`${value.t}`);
    setEvents({ alarm: value.e || "", recovery: value.r || "" });
  }, [value]);

  return (
    <Grid container spacing={1} padding={1}>
      <Grid item>
        <FormControl
          variant="filled"
          size="small"
          sx={{ margin: 0, minWidth: 200, width: 200 }}
          className={!saved || !Object.keys(saved).includes(ruleType) ? editedStyle.input : ""}
          disabled={disabled}
          fullWidth
        >
          <InputLabel id="behavior-select-label">{formatMessage({ defaultMessage: "Alarm rule type" })}</InputLabel>
          <Select labelId="behavior-select-label" value={ruleType} onChange={handleRuleTypeChange} color="secondary">
            <MenuItem value="min">{formatMessage({ defaultMessage: "Minimum rule" }, { type: "min" })}</MenuItem>
            <MenuItem value="max">{formatMessage({ defaultMessage: "Maximum rule" }, { type: "max" })}</MenuItem>
          </Select>
          <FormHelperText>
            {formatMessage({ defaultMessage: "Alarm {type, select, min {under} max {above} other {}} the given value." }, { type: ruleType })}
          </FormHelperText>
        </FormControl>
      </Grid>
      <Grid item>
        <TextField
          label={formatMessage({ defaultMessage: "Value ({measUnit})" }, { measUnit: ruleTypeObject?.measUnit })}
          variant="filled"
          value={v}
          className={(ruleType === "min" ? saved?.min : saved?.max) !== parseFloat(v) ? editedStyle.input : ""}
          inputProps={{ type: "number", onClick: onNumberInputClick, ...ruleTypeObject?.inputProps }}
          onChange={(e) => setV(e.target.value)}
          onBlur={() => {
            if (ruleType === "min") {
              value.min = v ? parseFloat(v) : undefined;
            } else {
              value.max = v ? parseFloat(v) : undefined;
            }
            onChange(index, value);
          }}
          color="secondary"
          sx={{ margin: 0, minWidth: 160, width: 160 }}
          size="small"
          autoComplete="off"
          disabled={disabled}
          fullWidth
        />
      </Grid>
      <Grid item>
        <TimeTextField
          label={formatMessage({ defaultMessage: "Delay" })}
          unit={1000}
          variant="filled"
          value={time}
          className={valueEdited(saved?.t, parseInt(time, 10), value.t) ? editedStyle.input : ""}
          inputProps={{ min: 0, type: "number", onClick: onNumberInputClick }}
          onChange={(e) => setTime(e.target.value)}
          onBlur={() => {
            if (time !== "") {
              value.t = parseInt(time, 10);
            } else {
              delete value.t;
            }
            onChange(index, value);
          }}
          color="secondary"
          sx={{ margin: 0, minWidth: 220, width: 220 }}
          size="small"
          autoComplete="off"
          disabled={disabled}
          fullWidth
        />
      </Grid>
      <ZoneEvent
        value={events}
        saved={saved ? { alarm: saved.e || "", recovery: saved.r || "" } : undefined}
        onChange={({ alarm, recovery }) => {
          setEvents({ alarm, recovery: recovery || "" });
          if (alarm) {
            value.e = alarm;
          } else {
            delete value.e;
          }
          value.r = recovery || "";
          onChange(index, value);
        }}
        emptyRecovery={""}
        disabled={disabled}
        color="secondary"
        xs="auto"
        width={220}
      />
    </Grid>
  );
};

export default AlarmItem;
